// src/components/store/sections/navbar/NavbarAmazonStyle.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { 
  Menu, 
  Search, 
  MapPin, 
  ShoppingCart, 
  ChevronDown, 
  X, 
  Package, 
  Sparkles, 
  Layers, 
  Flame, 
  ArrowRight, 
  ArrowLeft,
  Check, 
  Loader2, 
  Navigation,
  Store,
  Camera,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import UserHeaderMenu from "@/components/store/UserHeaderMenu";
import CartHeaderButton from "@/components/store/CartHeaderButton";
import ShopCardsModal, { ShopItem } from "@/components/store/sections/shop/ShopCardsModal";
import VisualSearchModal from "./VisualSearchModal";
import { useNavbarBranding, NavbarBrandIdentity, NavbarBrandingConfig } from "./brandingConfig";

interface NavbarProps {
  subdomain?: string;
  storeName?: string;
  logo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  contactAddress?: string;
  storeTagline?: string;
  brandingConfig?: Partial<NavbarBrandingConfig>;
  isPreview?: boolean;
}

const BANGLADESH_LOCATIONS = [
  { name: "Khulna", code: "9000", label: "Khulna (9000) - Khulna Sadar" },
  { name: "Dhaka", code: "1207", label: "Dhaka (1207) - Mohammadpur / Dhanmondi" },
  { name: "Dhaka", code: "1212", label: "Dhaka (1212) - Gulshan / Banani" },
  { name: "Dhaka", code: "1216", label: "Dhaka (1216) - Mirpur" },
  { name: "Chittagong", code: "4000", label: "Chittagong (4000) - Agrabad / GEC" },
  { name: "Sylhet", code: "3100", label: "Sylhet (3100) - Zindabazar" },
  { name: "Rajshahi", code: "6000", label: "Rajshahi (6000) - Boalia" },
  { name: "Barisal", code: "8200", label: "Barisal (8200) - Sadar" },
  { name: "Rangpur", code: "5400", label: "Rangpur (5400) - Modern Mor" },
  { name: "Gazipur", code: "1700", label: "Gazipur (1700) - Joydebpur" },
  { name: "Comilla", code: "3500", label: "Comilla (3500) - Kandirpar" },
];

export default function NavbarAmazonStyle({
  subdomain = "nextlook",
  storeName = "NextLook",
  logo,
  primaryColor = "#059669",
  secondaryColor = "#047857",
  contactAddress = "Khulna 9000",
  storeTagline,
  brandingConfig,
}: NavbarProps) {
  const {
    branding,
    effectiveStoreName,
    effectiveTagline,
    effectiveLogo,
    effectivePrimaryColor,
    effectiveSecondaryColor,
    effectiveContactAddress,
    effectiveAnnouncementText,
    effectiveAnnouncementPhone,
    effectiveShowAnnouncementBar,
    isCustomColor,
    topBarStyle,
    mainBarStyle,
    subBarStyle,
  } = useNavbarBranding({
    subdomain,
    storeName,
    storeTagline,
    logo,
    primaryColor,
    secondaryColor,
    contactAddress,
    brandingConfig,
  });

  storeName = effectiveStoreName || storeName;
  logo = effectiveLogo;
  primaryColor = effectivePrimaryColor;
  secondaryColor = effectiveSecondaryColor;
  contactAddress = effectiveContactAddress;
  const router = useRouter();
  const cartContext = useCart?.() as any;

  const totalItems = 
    typeof cartContext?.totalItems === "number"
      ? cartContext.totalItems
      : typeof cartContext?.total_items === "number"
      ? cartContext.total_items
      : Array.isArray(cartContext?.cart)
      ? cartContext.cart.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 1), 0)
      : Array.isArray(cartContext?.cartItems)
      ? cartContext.cartItems.reduce((sum: number, item: any) => sum + (Number(item?.quantity) || 1), 0)
      : 0;

  // Shop States
  const [shopsList, setShopsList] = useState<ShopItem[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>("all");
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [isAllShopsModalOpen, setIsAllShopsModalOpen] = useState(false);
  const shopDropdownRef = useRef<HTMLDivElement>(null);

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; name: string }>({
    id: "all",
    name: "All",
  });
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Mobile Search & Visual Search States
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);

  const searchBoxRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  // Delivery Location states
  const [currentLocation, setCurrentLocation] = useState(contactAddress);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [customZipInput, setCustomZipInput] = useState("");

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const shopParam = params.get("shop");
      if (shopParam) {
        setSelectedShopId(shopParam);
      } else {
        setSelectedShopId("all");
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("store_delivery_location");
      if (saved) {
        setCurrentLocation(saved);
      }
    }
  }, []);

  // Shops Fetch
  useEffect(() => {
    async function fetchShops() {
      setShopsLoading(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const apiUrl = `${apiBase}/api/v1/stores/shops/`;

        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("merchant_token") ||
              localStorage.getItem("token") ||
              localStorage.getItem("customer_token") ||
              ""
            : "";

        const res = await fetch(apiUrl, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.shops || [];
          setShopsList(list);
        }
      } catch (err) {
        console.error("Error fetching shops in Navbar:", err);
      } finally {
        setShopsLoading(false);
      }
    }
    fetchShops();
  }, [subdomain]);

  // Categories Fetch
  useEffect(() => {
    async function fetchCategories() {
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiUrl = isLocal
          ? `http://${subdomain}.localhost:8000/api/v1/products/categories/`
          : `/api/v1/products/categories/`;

        const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.results || [];
          setCategoriesList(list);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    }
    fetchCategories();
  }, [subdomain]);

  // Live Auto-Suggestion Search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const params = new URLSearchParams({ search: searchQuery.trim() });
        if (selectedCategory.id !== "all") {
          params.append("category", selectedCategory.id);
        }
        if (selectedShopId !== "all") {
          params.append("shop", selectedShopId);
        }

        const apiUrl = isLocal
          ? `http://${subdomain}.localhost:8000/api/v1/products/?${params.toString()}`
          : `/api/v1/products/?${params.toString()}`;

        const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.results || [];
          setSuggestions(list.slice(0, 6));
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, selectedShopId, subdomain]);

  // Click outside listener
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setIsCategoryMenuOpen(false);
      }
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(e.target as Node)) {
        setIsShopMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectLocation = (locText: string) => {
    setCurrentLocation(locText);
    if (typeof window !== "undefined") {
      localStorage.setItem("store_delivery_location", locText);
    }
    setIsLocationModalOpen(false);
  };

  const handleSelectShop = (shopId: string | number) => {
    setIsShopMenuOpen(false);
    setIsAllShopsModalOpen(false);
    setSelectedShopId(String(shopId));
    if (shopId === "all") {
      router.push("/products");
    } else {
      router.push(`/products?shop=${shopId}`);
    }
  };

  const currentSelectedShop = shopsList.find((s) => String(s.id) === selectedShopId);
  const currentShopLabel = currentSelectedShop ? currentSelectedShop.name : "All Outlets";

  const handleOpenCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const hiddenBtn = document.querySelector("#amazon-hidden-cart-btn button") as HTMLButtonElement;
    if (hiddenBtn) {
      hiddenBtn.click();
      return;
    }

    if (cartContext) {
      if (typeof cartContext.setIsCartOpen === "function") cartContext.setIsCartOpen(true);
      if (typeof cartContext.openCart === "function") cartContext.openCart();
      if (typeof cartContext.setIsOpen === "function") cartContext.setIsOpen(true);
      if (typeof cartContext.toggleCart === "function") cartContext.toggleCart();
    }

    window.dispatchEvent(new CustomEvent("openCartDrawer"));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    setIsMobileSearchOpen(false);
    if (searchQuery.trim()) {
      const catParam = selectedCategory.id !== "all" ? `&category=${selectedCategory.id}` : "";
      const shopParam = selectedShopId !== "all" ? `&shop=${selectedShopId}` : "";
      router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}${catParam}${shopParam}`);
    }
  };

  const resolveImgUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  return (
    <header className="sticky top-0 z-50 w-full font-sans shadow-md">
      <div id="amazon-hidden-cart-btn" className="hidden" aria-hidden="true">
        <CartHeaderButton />
      </div>

      {/* 0. Optional Top Announcement Strip when Custom Colors or Announcement Enabled */}
      {effectiveShowAnnouncementBar && isCustomColor && (
        <div
          style={topBarStyle}
          className="w-full text-white text-[11px] font-bold py-1.5 px-3 sm:px-6 flex items-center justify-between"
        >
          <span className="truncate">{effectiveAnnouncementText}</span>
          <span className="hidden sm:inline font-mono text-[10px] shrink-0">{effectiveAnnouncementPhone}</span>
        </div>
      )}

      {/* 1. Top Navigation Bar (Amazon Dark Navy #131921) */}
      <div
        style={mainBarStyle}
        className="bg-[#131921] text-white px-2.5 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-4 relative z-30 min-h-[52px]"
      >
        
        {/* 1.A. MOBILE FULL-WIDTH SEARCH OVERLAY */}
        {isMobileSearchOpen && (
          <div className="absolute inset-0 z-50 bg-[#131921] px-2.5 py-1.5 flex items-center gap-2 animate-in fade-in duration-150">
            <button
              type="button"
              onClick={() => {
                setIsMobileSearchOpen(false);
                setShowSuggestions(false);
              }}
              className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <form 
              onSubmit={handleSearchSubmit}
              className="flex-1 flex items-center h-9.5 bg-white rounded-md overflow-hidden shadow-md"
            >
              <input
                ref={mobileSearchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, brands and more..."
                className="flex-1 h-full px-3 text-xs text-slate-900 placeholder-slate-500 bg-transparent focus:outline-hidden"
              />

              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setSuggestions([]);
                  }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* 🌟 Mobile Visual Search Camera Button */}
              <button
                type="button"
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setIsVisualSearchOpen(true);
                }}
                title="Search by Photo or Camera"
                className="p-2 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer shrink-0"
              >
                <Camera className="w-4 h-4" style={{ color: primaryColor }} />
              </button>

              <button
                type="submit"
                style={{ backgroundColor: primaryColor }}
                className="h-full px-3.5 text-white flex items-center justify-center shrink-0 cursor-pointer"
              >
                <Search className="w-4 h-4 stroke-[2.5]" />
              </button>
            </form>
          </div>
        )}

        {/* Brand Logo & Name */}
        <NavbarBrandIdentity
          href="/"
          branding={branding}
          storeName={storeName}
          tagline={effectiveTagline}
          logo={logo}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          isDarkTheme={true}
          variantStyle="amazon"
          className="p-1 rounded-xs border border-transparent hover:border-white"
        />

        {/* 2. Location Widget */}
        <div 
          onClick={() => setIsLocationModalOpen(true)}
          className="hidden md:flex items-center gap-1.5 p-1.5 rounded-xs border border-transparent hover:border-white transition-all cursor-pointer shrink-0 group select-none"
          title="Click to update delivery location"
        >
          <MapPin 
            style={{ color: primaryColor }}
            className="w-4 h-4 mt-0.5 shrink-0 transition-colors" 
          />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] text-slate-300 font-normal">Deliver to</span>
            <div className="flex items-center gap-0.5 text-xs font-bold text-white">
              <span className="truncate max-w-[90px]">{currentLocation}</span>
              <ChevronDown className="w-3 h-3 text-slate-400 group-hover:text-white" />
            </div>
          </div>
        </div>

        {/* 3. Select Outlet Dropdown */}
        <div ref={shopDropdownRef} className="relative hidden lg:flex items-center shrink-0">
          <button
            type="button"
            onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
            className="flex items-center gap-1.5 p-1.5 rounded-xs border border-transparent hover:border-white transition-all cursor-pointer group select-none text-left"
            title="Filter by Shop / Outlet"
          >
            <Store style={{ color: primaryColor }} className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] text-slate-300 font-normal">Select Outlet</span>
              <div className="flex items-center gap-0.5 text-xs font-bold text-white">
                <span className="truncate max-w-[105px]">{currentShopLabel}</span>
                <ChevronDown className={`w-3 h-3 text-slate-400 group-hover:text-white transition-transform ${isShopMenuOpen ? "rotate-180" : ""}`} />
              </div>
            </div>
          </button>

          {isShopMenuOpen && (
            <div className="absolute top-full left-0 mt-1.5 w-60 bg-white rounded-xl shadow-2xl border border-slate-200 z-[999] py-1 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
              <div className="px-3.5 py-1.5 border-b border-slate-100 text-[10px] font-black uppercase tracking-wider text-slate-400">
                Select Shop / Outlet
              </div>
              <button
                type="button"
                onClick={() => handleSelectShop("all")}
                className={`w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                  selectedShopId === "all" ? "bg-slate-100 font-black" : ""
                }`}
                style={selectedShopId === "all" ? { color: primaryColor } : undefined}
              >
                <span>🏪 Main Store (All)</span>
                {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
              </button>

              {shopsLoading ? (
                <div className="px-3.5 py-2.5 text-xs text-slate-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Loading outlets...</span>
                </div>
              ) : shopsList.length === 0 ? (
                <div className="px-3.5 py-2 text-xs text-slate-400 font-medium">
                  No other outlets found
                </div>
              ) : (
                shopsList.map((shop) => (
                  <button
                    key={shop.id}
                    type="button"
                    onClick={() => handleSelectShop(shop.id)}
                    className={`w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate ${
                      String(shop.id) === selectedShopId ? "bg-slate-100 font-bold" : "text-slate-700"
                    }`}
                    style={String(shop.id) === selectedShopId ? { color: primaryColor } : undefined}
                  >
                    <div className="truncate">
                      <span className="truncate block font-bold">{shop.name}</span>
                      {shop.category_label && (
                        <span className="text-[9px] text-slate-400 font-mono block">
                          {shop.category_label}
                        </span>
                      )}
                    </div>
                    {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* 3.1 "Shops" Button */}
        <div className="hidden lg:flex items-center shrink-0">
          <button
            type="button"
            onClick={() => setIsAllShopsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-white/20 bg-gradient-to-r from-purple-600/30 to-pink-600/30 hover:from-purple-600/50 hover:to-pink-600/50 text-white transition-all cursor-pointer group select-none shadow-xs"
            title="Explore All Shops & Stories"
          >
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse shrink-0" />
            <div className="flex flex-col leading-tight text-left">
              <span className="text-[9px] text-slate-300 font-normal uppercase tracking-wider">Explore</span>
              <span className="text-xs font-black text-white flex items-center gap-0.5">
                <span>Shops</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 ml-0.5" />
              </span>
            </div>
          </button>
        </div>

        {/* 4. DESKTOP AMAZON SEARCH BAR */}
        <div ref={searchBoxRef} className="hidden md:flex flex-1 min-w-0 max-w-3xl relative">
          <form 
            onSubmit={handleSearchSubmit}
            className="w-full flex items-center h-10 rounded-md overflow-visible bg-white focus-within:ring-2 transition-all relative"
            style={{ ringColor: primaryColor }}
          >
            {/* Custom Category Selector */}
            <div ref={categoryDropdownRef} className="relative h-full shrink-0">
              <button
                type="button"
                onClick={() => setIsCategoryMenuOpen(!isCategoryMenuOpen)}
                className="h-full bg-[#f3f3f3] hover:bg-[#e2e2e2] text-slate-700 border-r border-slate-300 px-3 flex items-center gap-1.5 text-xs font-medium cursor-pointer transition-colors select-none rounded-l-md"
              >
                <span className="truncate max-w-[90px]">{selectedCategory.name}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${isCategoryMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {isCategoryMenuOpen && (
                <div className="absolute top-full left-0 mt-1 w-60 max-h-72 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-y-auto z-[999] py-1 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategory({ id: "all", name: "All" });
                      setIsCategoryMenuOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                      selectedCategory.id === "all" ? "bg-slate-100 font-black" : ""
                    }`}
                    style={selectedCategory.id === "all" ? { color: primaryColor } : undefined}
                  >
                    <span>All Categories</span>
                    {selectedCategory.id === "all" && <Check className="w-3.5 h-3.5 shrink-0" />}
                  </button>

                  <div className="my-1 border-t border-slate-100" />

                  {categoriesList.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        setSelectedCategory({ id: cat.slug || String(cat.id), name: cat.name });
                        setIsCategoryMenuOpen(false);
                      }}
                      className={`w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate ${
                        selectedCategory.id === (cat.slug || String(cat.id))
                          ? "bg-slate-100 font-bold"
                          : "text-slate-600 font-medium"
                      }`}
                      style={selectedCategory.id === (cat.slug || String(cat.id)) ? { color: primaryColor } : undefined}
                    >
                      <span className="truncate">{cat.name}</span>
                      {selectedCategory.id === (cat.slug || String(cat.id)) && (
                        <Check className="w-3.5 h-3.5 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Live Search Input */}
            <input
              type="text"
              value={searchQuery}
              onFocus={() => {
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products, brands and more..."
              className="flex-1 min-w-0 h-full px-3 text-sm text-slate-900 placeholder-slate-500 bg-transparent focus:outline-hidden"
            />

            {isSearching ? (
              <Loader2 className="w-4 h-4 text-slate-400 animate-spin mr-1.5 shrink-0" />
            ) : searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setSuggestions([]);
                }}
                className="p-1 mr-1 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}

            {/* 🌟 Desktop Visual Search Camera / Lens Button */}
            <button
              type="button"
              onClick={() => {
                setShowSuggestions(false);
                setIsVisualSearchOpen(true);
              }}
              title="Visual Search (Upload Photo or Use Camera)"
              className="p-2 mx-1 rounded-full text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer flex items-center justify-center shrink-0"
            >
              <Camera className="w-4.5 h-4.5" style={{ color: primaryColor }} />
            </button>

            <button
              type="submit"
              style={{ backgroundColor: primaryColor }}
              className="h-full px-5 text-white hover:opacity-95 active:scale-95 transition-all flex items-center justify-center cursor-pointer shrink-0 rounded-r-md"
              aria-label="Search"
            >
              <Search className="w-4 h-4 stroke-[2.5]" />
            </button>
          </form>

          {/* Desktop Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[998] text-slate-900 animate-in fade-in slide-in-from-top-1 duration-150">
              <div className="p-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider px-3">
                <span>Matching Products</span>
                <span>{suggestions.length} results</span>
              </div>

              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {suggestions.map((item) => {
                  const img = resolveImgUrl(item.primary_image || item.image);
                  return (
                    <Link
                      key={item.id}
                      href={`/products/${item.slug || item.id}`}
                      onClick={() => setShowSuggestions(false)}
                      className="flex items-center gap-3 p-2.5 hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-slate-100 relative overflow-hidden shrink-0 border border-slate-200">
                        {img ? (
                          <img src={img} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-5 h-5 text-slate-400 m-auto mt-2.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-xs font-bold text-slate-800 truncate">{item.name}</h5>
                        <p className="text-[10px] text-slate-400 line-clamp-1">{item.category_name || "General"}</p>
                      </div>
                      <div 
                        style={{ color: primaryColor }}
                        className="text-xs font-black shrink-0"
                      >
                        ৳{Math.round(item.price).toLocaleString()}
                      </div>
                    </Link>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleSearchSubmit}
                style={{ color: primaryColor }}
                className="w-full py-2.5 bg-slate-50 hover:bg-slate-100 text-center text-xs font-bold border-t border-slate-100 flex items-center justify-center gap-1 cursor-pointer"
              >
                <span>View all search results for "{searchQuery}"</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Mobile Camera Icon Trigger */}
          <button
            type="button"
            onClick={() => setIsVisualSearchOpen(true)}
            className="hidden p-2 rounded-lg text-slate-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title="Visual Search"
            aria-label="Visual Search"
          >
            <Camera className="w-5 h-5" style={{ color: primaryColor }} />
          </button>

          {/* Mobile Search Trigger Button */}
          <button
            type="button"
            onClick={() => setIsMobileSearchOpen(true)}
            className="md:hidden p-2 rounded-lg text-slate-200 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title="Search"
            aria-label="Open search"
          >
            <Search className="w-5 h-5 stroke-[2.2]" />
          </button>

          <div className="hidden sm:flex items-center relative z-50">
            <UserHeaderMenu />
          </div>

          <Link
            href="/dashboard"
            className="hidden md:flex flex-col leading-tight p-1.5 rounded-xs border border-transparent hover:border-white transition-all text-left"
          >
            <span className="text-[11px] text-slate-300 font-normal">Returns</span>
            <span className="text-xs font-bold text-white">& Orders</span>
          </Link>

          <button
            type="button"
            onClick={handleOpenCart}
            className="flex items-center gap-1 p-1 sm:p-1.5 rounded-xs border border-transparent hover:border-white transition-all cursor-pointer relative shrink-0"
            title="Shopping Cart"
          >
            <div className="relative flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 sm:w-7 sm:h-7 text-white stroke-[1.8]" />
              <span 
                style={{ color: primaryColor }}
                className="absolute -top-1 left-1/2 -translate-x-1/2 text-[11px] sm:text-xs font-black"
              >
                {totalItems}
              </span>
            </div>
            <span className="hidden sm:inline font-bold text-xs sm:text-sm text-white self-end mb-0.5">
              Cart
            </span>
          </button>
        </div>
      </div>

      {/* 2. Sub-Navigation Bar */}
      <div
        style={subBarStyle}
        className="bg-[#232f3e] text-white px-2.5 sm:px-4 py-1.5 flex items-center justify-between text-xs sm:text-sm font-medium relative z-20"
      >
        <div className="flex items-center gap-1.5 sm:gap-2.5 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden w-full">
          
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-xs border border-transparent hover:border-white transition-all font-bold cursor-pointer shrink-0"
          >
            <Menu className="w-4 h-4 stroke-[2.2]" />
            <span>All</span>
          </button>

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-xs transition-all cursor-pointer shrink-0"
            >
              <Store className="w-3.5 h-3.5" style={{ color: primaryColor }} />
              <span className="truncate max-w-[85px] sm:max-w-[100px]">{currentShopLabel}</span>
              <ChevronDown className="w-3 h-3 text-slate-300" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsAllShopsModalOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 text-purple-200 font-bold text-xs transition-all cursor-pointer shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span>All Shops</span>
          </button>

          <Link
            href="/products?deals=true"
            className="px-2 py-1 rounded-xs border border-transparent hover:border-white transition-all shrink-0 hover:opacity-80"
            style={{ color: primaryColor }}
          >
            Today's Deals
          </Link>

          <Link
            href="/customer-service"
            className="px-2 py-1 rounded-xs border border-transparent hover:border-white transition-all shrink-0"
          >
            Customer Service
          </Link>

          <Link
            href="/products"
            className="hidden sm:inline px-2 py-1 rounded-xs border border-transparent hover:border-white transition-all shrink-0"
          >
            Gift Cards
          </Link>

          <Link
            href="/sell"
            className="hidden md:inline px-2 py-1 rounded-xs border border-transparent hover:border-white transition-all shrink-0"
          >
            Sell
          </Link>

          <Link
            href="/categories"
            className="sm:hidden px-2 py-1 text-xs hover:text-white rounded-xs border border-transparent hover:border-white transition-all shrink-0"
            style={{ color: primaryColor }}
          >
            Categories
          </Link>
        </div>

        <Link
          href="/categories"
          className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs hover:text-white rounded-xs border border-transparent hover:border-white transition-all shrink-0 whitespace-nowrap ml-2"
          style={{ color: primaryColor }}
        >
          <span>Shop by Category</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* 3. SHOP COMPONENT MODAL */}
      <ShopCardsModal
        isOpen={isAllShopsModalOpen}
        onClose={() => setIsAllShopsModalOpen(false)}
        shopsList={shopsList}
        shopsLoading={shopsLoading}
        onSelectShop={handleSelectShop}
        primaryColor={primaryColor}
      />

      {/* 🌟 3.5 AI VISUAL SEARCH MODAL */}
      <VisualSearchModal
        isOpen={isVisualSearchOpen}
        onClose={() => setIsVisualSearchOpen(false)}
        subdomain={subdomain}
        primaryColor={primaryColor}
      />

      {/* 4. Location Selector Popup Modal */}
      {isLocationModalOpen && (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            onClick={() => setIsLocationModalOpen(false)}
          />

          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-4 sm:p-6 z-10 text-slate-900 animate-in zoom-in-95 duration-200 space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
                <h3 className="text-sm sm:text-base font-black text-slate-900">Choose delivery location</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsLocationModalOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed">
              Available items and fees will be updated according to your area.
            </p>

            <div className="space-y-1.5 max-h-52 sm:max-h-56 overflow-y-auto pr-1">
              {BANGLADESH_LOCATIONS.map((loc, idx) => {
                const isSelected = currentLocation.toLowerCase().includes(loc.name.toLowerCase()) && 
                                   currentLocation.includes(loc.code);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectLocation(`${loc.name} ${loc.code}`)}
                    className={`w-full p-2.5 rounded-xl border text-left text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                      isSelected
                        ? "border-emerald-500 bg-emerald-50 text-emerald-900 shadow-xs"
                        : "border-slate-200 hover:bg-slate-50 text-slate-700"
                    }`}
                    style={isSelected ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15`, color: primaryColor } : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <Navigation className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                      <span className="truncate">{loc.label}</span>
                    </div>
                    {isSelected && <Check className="w-4 h-4 shrink-0" style={{ color: primaryColor }} />}
                  </button>
                );
              })}
            </div>

            <div className="pt-2 border-t border-slate-100 flex gap-2">
              <input
                type="text"
                value={customZipInput}
                onChange={(e) => setCustomZipInput(e.target.value)}
                placeholder="Area or Postcode (e.g. 9100)..."
                className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-hidden"
                style={{ borderColor: customZipInput ? primaryColor : undefined }}
              />
              <button
                type="button"
                disabled={!customZipInput.trim()}
                onClick={() => {
                  if (customZipInput.trim()) {
                    handleSelectLocation(customZipInput.trim());
                    setCustomZipInput("");
                  }
                }}
                style={{ backgroundColor: primaryColor }}
                className="px-3.5 sm:px-4 py-2 text-white text-xs font-bold rounded-xl transition-all disabled:opacity-50 cursor-pointer shadow-xs"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. Mobile Drawer */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-[999999] flex animate-in fade-in duration-200">
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileDrawerOpen(false)}
          />

          <div className="relative w-72 sm:w-80 max-w-[85vw] bg-white text-slate-900 h-full shadow-2xl flex flex-col justify-between z-10 animate-in slide-in-from-left duration-300">
            <div>
              <div className="bg-[#232f3e] text-white p-3.5 sm:p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    style={{ backgroundColor: primaryColor }}
                    className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs text-white"
                  >
                    {storeName.charAt(0)}
                  </div>
                  <span className="font-bold text-sm">Hello, Sign In</span>
                </div>
                <button
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-1 rounded-md hover:bg-slate-700 text-slate-300 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-3.5 sm:p-4 space-y-4 overflow-y-auto max-h-[80vh]">
                {shopsList.length > 0 && (
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
                      Shops & Outlets
                    </h4>
                    <div className="space-y-1 text-xs font-bold text-slate-700">
                      <button
                        type="button"
                        onClick={() => {
                          handleSelectShop("all");
                          setMobileDrawerOpen(false);
                        }}
                        className={`w-full flex items-center justify-between p-2 rounded-xl text-left ${
                          selectedShopId === "all" ? "bg-slate-100 font-black text-emerald-600" : "hover:bg-slate-50"
                        }`}
                      >
                        <span>🏪 Main Store (All)</span>
                        {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
                      </button>
                      {shopsList.map((shop) => (
                        <button
                          key={shop.id}
                          type="button"
                          onClick={() => {
                            handleSelectShop(shop.id);
                            setMobileDrawerOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-2 rounded-xl text-left ${
                            String(shop.id) === selectedShopId
                              ? "bg-slate-100 font-black text-emerald-600"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="truncate">
                            <span className="block font-bold">{shop.name}</span>
                            {shop.category_label && (
                              <span className="text-[9px] text-slate-400 font-normal">{shop.category_label}</span>
                            )}
                          </div>
                          {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-slate-100 pt-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Trending</h4>
                  <div className="space-y-1 text-sm font-semibold text-slate-700">
                    <Link
                      href="/products?bestseller=true"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Best Sellers</span>
                      <Flame className="w-4 h-4 text-amber-500" />
                    </Link>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Shop By Department</h4>
                  <div className="space-y-1 text-sm font-semibold text-slate-700">
                    <Link
                      href="/categories"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>All Categories</span>
                      <Layers className="w-4 h-4 text-slate-400" />
                    </Link>
                    <Link
                      href="/products?deals=true"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Today's Deals</span>
                      <Package className="w-4 h-4 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-3.5 sm:p-4 border-t border-slate-100 text-[11px] text-slate-400 font-mono text-center">
              © {storeName} • Storefront Navigation
            </div>
          </div>
        </div>
      )}
    </header>
  );
}